/*** IMPORTS FROM imports-loader ***/
var fetchPolyfill = require("polyfills/fetch");
var classListPolyfill = require("polyfills/classList");

import baustein from "baustein";
import analytics from "analytics/analytics";
import browser from "utils/browser";
import newInfiniteScrollMixin from "./mixins/infinite-scroll";
import ScrollToTopButton from "components/scroll-to-top-button";
import history from "utils/history";
import style from "dom/style";
import rothko from "modules/rothko";
import factory from "dom/factory";
import userProfiler from "../modules/userprofiler";
import feedSurveyTemplate from "../templates/surveys/feed-survey";
import Overlay from "../utils/overlay";
import globalEvents from "utils/global-events";
import globals from "modules/globals";
import template from "templates/modules/product_feed";
import filterPreviewsTemplate from "templates/modules/filter_previews_suggested_designer";
import oosHeaderTemplate from "templates/modules/product_feed_oos_section_header";
import url from "utils/url";
import { languageIsEnglish } from "modules/language";

const EMPTY_FEED = 0;
const THIN_FEED_PRODUCT_LIMIT = 7;
const CRITEO_PRODUCT_ID_LIMIT = 3;
const FEED_SURVEY_PAGE = 7;

let feedPageCount = 1;
export let hasProductCardInteraction = false;
let feedSurveyOverlay;

export default baustein.register("product-feed", {
    template,

    mixins: [newInfiniteScrollMixin],

    getInitialRenderContext() {
        return {
            product_feed: this.options.feedData,
            modifier: this.options.modifier,
            feed_sort_dropdown: this.options.feedSortDropdown
        };
    },

    setupEvents(add) {
        add("openOverlay", "product-card", this._onOpenProductOverlay);
        add("infinite-scroll-end", this._onInfiniteScrollEnd);
        add("productCardInteraction", "product-card", this._onProductCardInteraction);
    },

    init() {
        this._seenProductIds = new Set();
        this._endOfFeedReached = false;
        this._infiniteScrollEnd = false;
        // flag to indicate if suggested designer filter previews is already called
        this._fetchedSuggestedDesigner = false;

        this._rothkoClient = new rothko.Rothko("modules/product_feed");

        if (!browser.supportSticky) {
            // if browser does not support 'position: sticky' use old buttons
            const bottomFilterButton = this.el.querySelector(
                ".product-feed__floating-filter-button"
            );
            if (bottomFilterButton) {
                bottomFilterButton.classList.add("hidden");
            }

            const topFilterButtons = this.el.querySelector(".product-feed__sticky-filter");
            if (topFilterButtons) {
                topFilterButtons.classList.add("hidden");
            }

            const oldSort = this.el.querySelector(".product-feed__sort");
            if (oldSort) {
                oldSort.classList.add("product-feed__sort--ie");
            }
        }
    },

    /**
     * Returns a promise that when resolved, returns suggested designer filter previews
     * as DocumentFragment object
     */
    _fetchSuggestedDesignerFragment(includeOOSHeader) {
        const parsedUrl = url.parse(globals.window.location.href);
        const queryParams = parsedUrl.searchParams;
        queryParams.set("ref", parsedUrl.path);
        return rothko
            .fetch("modules/filter_previews_suggested_designer", queryParams)
            .then(data => {
                const renderData = {
                    filter_previews: data
                };

                const fragment = document.createDocumentFragment();
                // create filter previews
                const filterPreviews = factory.fromString(filterPreviewsTemplate(renderData));

                if (!filterPreviews) {
                    return fragment;
                }

                fragment.appendChild(filterPreviews);

                if (includeOOSHeader) {
                    // create OOS section header
                    const oosHeader = factory.fromString(oosHeaderTemplate());
                    fragment.appendChild(oosHeader);
                }
                return fragment;
            });
    },

    /**
     * Returns the first OOS product card component
     */
    _findFirstOOSProdcut() {
        const productCards = this.findComponents("product-card");
        return productCards.find(product => {
            return !product.options.inStock;
        });
    },

    /**
     * Insert suggested designers filter previews within any designer based feed
     * It will either be placed before the 1st OOS products or at the very end of feed (last page)
     */
    async insertSuggestedDesigners() {
        // as long as we've called fetch once, regardless of whether we successfully render
        // the related previews or not, we don't call it again.
        if (this._fetchedSuggestedDesigner) {
            return;
        }

        const oosProduct = this._findFirstOOSProdcut();
        if (oosProduct) {
            const suggestedDesignerFragment = await this._fetchSuggestedDesignerFragment(true);
            this._fetchedSuggestedDesigner = true;
            oosProduct.el.parentNode.insertBefore(suggestedDesignerFragment, oosProduct.el);
        } else if (
            // if it's end of feed, no oos products, and no suggested designers inserted
            this.options.pagination === null ||
            this.options.pagination.current_page === this.options.pagination.total_pages
        ) {
            const suggestedDesignerFragment = await this._fetchSuggestedDesignerFragment(false);
            this._fetchedSuggestedDesigner = true;
            this.el.appendChild(suggestedDesignerFragment);
        }
    },

    onInsert() {
        const scrollToTopButton = new ScrollToTopButton();
        scrollToTopButton.appendTo(this);

        this.insertSuggestedDesigners();
        const productCards = this.findComponents("product-card");
        // Save the first slice of product ids to avoid duplicates in the next pages
        productCards.forEach(product => this._seenProductIds.add(product.options.productId));
        if (productCards.length === EMPTY_FEED) {
            // don't send the end of feed event
            this._endOfFeedReached = true;
            analytics.event("ENGAGEMENT", "FEED_END", "EMPTY_FEED_LANDING", true);
        } else if (
            productCards.length < THIN_FEED_PRODUCT_LIMIT &&
            analytics.getPageViewsCount() === 1
        ) {
            analytics.event(
                "ENGAGEMENT",
                "FEED_END",
                "THIN_FEED_LANDING",
                productCards.length,
                true
            );
        }

        // check if the user is already at the end of the feed, for tracking
        this._onScroll();

        // and add the event handler
        this.setGlobalHandler("scroll", this._onScroll);

        // Track top of page product ids for criteo remarketing
        if (productCards.length > 0) {
            const criteoProductIds = [];

            productCards.slice(0, CRITEO_PRODUCT_ID_LIMIT).forEach(product => {
                criteoProductIds.push(product.options.productId);
            });

            analytics.tagEvent(analytics.TAG_NETWORKS.CRITEO, "criteo_product_ids", {
                ids: criteoProductIds
            });
        }

        this.emit("showCsaUnit", { position: "top" });
    },

    onRemove() {
        this.releaseGlobalHandler("scroll", this._onScroll);
    },

    /**
     * This method is called by the infinite-scroll mixin when more content should be loaded.
     * @returns {Promise}
     */
    loadMoreContent(url) {
        if (!url) {
            return Promise.resolve();
        }

        feedPageCount++;
        this.showFeedSurveyForValidConditions();

        return this._rothkoClient.fetch({ url }).then(productFeedData => {
            const pagination = productFeedData.pagination;
            this.updateOptions({ pagination });
            this.renderData(productFeedData);
            // return the next url for the infinite-scroll mixin to store
            return pagination ? pagination.next_button_href : null;
        });
    },

    showFeedSurveyForValidConditions() {
        if (
            this.options.features &&
            this.options.features.show_feed_survey &&
            languageIsEnglish()
        ) {
            const surveyShown = userProfiler.getSurveyShown();
            if (!surveyShown && feedPageCount == FEED_SURVEY_PAGE && !hasProductCardInteraction) {
                this.showFeedSurvey();
            }
        }
    },

    showFeedSurvey() {
        feedSurveyOverlay = new Overlay({
            cssClasses: ["size-m", "white"],
            content: feedSurveyTemplate({ question: "Struggling to find what you want?" }),
            onRenderFinish: () => {
                analytics.event("feed-survey", "open");
            },
            onOverlayClose: () => {
                analytics.event("feed-survey", "close");
            }
        });

        globalEvents.on("feed-survey-closed", this.closeFeedSurvey);

        userProfiler.setSurveyShown();
    },

    closeFeedSurvey() {
        feedSurveyOverlay.close();
        globalEvents.off("feed-survey-closed", this.closeFeedSurvey);
    },

    renderData(data) {
        data.feed_items = data.feed_items.filter(item => {
            if (item.product_card && this._seenProductIds.has(item.product_card.id)) {
                return false;
            }

            this._seenProductIds.add(item.product_card.id);
            return true;
        });

        const renderData = {
            product_feed: data,
            feed_sort_dropdown: this.options.feedSortDropdown
        };

        const fragment = document.createDocumentFragment();
        const productFeed = factory.fromString(this.template(renderData));

        let segments = Array.from(productFeed.querySelectorAll(".product-feed__segment"));
        if (!segments.length) {
            return null;
        }

        segments.forEach(segment => {
            const segmentTitle = segment.getAttribute("segment-title") || "";
            const existing = this.el.querySelector(`[segment-title="${segmentTitle}"]`);

            if (existing) {
                const itemsFragment = document.createDocumentFragment();

                Array.from(segment.querySelector(".product-feed__segment-items").children).forEach(
                    c => {
                        itemsFragment.appendChild(c);
                    }
                );

                // append all the items to the fragment
                existing.querySelector(".product-feed__segment-items").appendChild(itemsFragment);

                return;
            }

            fragment.appendChild(segment);
        });

        const productFeedContainer = this.el.querySelector(".product-feed");
        productFeedContainer.appendChild(fragment);

        this.insertSuggestedDesigners();

        // return the next url for the infinite-scroll mixin to store
        return data.pagination.next_button_href;
    },

    _onOpenProductOverlay(event) {
        history.pushState(event.url, event.queryParams);
        hasProductCardInteraction = true;
    },

    _onInfiniteScrollEnd() {
        this._infiniteScrollEnd = true;
    },

    /**
     * Temporary function that monitors when the user gets to the end of the feed. We want to
     * track when this happens. It's called on scroll.
     * @private
     */
    _onScroll() {
        const bottomPos = style.getPosition(this.el).bottom;
        const difference = browser.getWindowHeight() - bottomPos;

        if (this._infiniteScrollEnd && !this._endOfFeedReached && difference > 0) {
            this._endOfFeedReached = true;
            analytics.event("ENGAGEMENT", "FEED_END", "END_OF_FEED_REACHED", true);
            this.emit("showCsaUnit", { position: "end" });
        }
    },

    /**
     * This turns the product feed into "loading" mode. There isn't a way out of it, since
     * when the loading is complete, a new product feed is created instead that has a fresh state.
     */
    startLoading() {
        this.el.classList.add("product-feed--loading");
    },

    _onProductCardInteraction() {
        hasProductCardInteraction = true;
    }
});

